/**
 * Typed, PHI-safe telemetry facade over Datadog RUM.
 *
 * Centralizes custom RUM actions and feature-flag evaluations so feature code
 * never touches `datadogRum` directly. Two guarantees are enforced here:
 *
 *  1. **Credential guard** — every call is a no-op when Datadog credentials are
 *     absent (local dev / tests), mirroring `initializeDatadog.ts`.
 *  2. **PHI safety** — only primitive identifiers and measures are ever sent.
 *     Objects, arrays, and over-long strings are dropped. Full FHIR resources
 *     (which are objects) can never leak through. See `.cursor/rules/phi-handling.mdc`.
 *
 * @agentic connections
 */

import { datadogRum } from "@datadog/browser-rum";

/**
 * Stable RUM custom-action names. Keeping them in one enum makes the Datadog
 * dashboards/monitors (see `apps/connections/docs/OBSERVABILITY.md`) refactor-safe.
 * @agentic connections
 */
export const TelemetryActions = {
  SMSSendAccepted: "sms.send.accepted",
  ScribeStarted: "scribe.prefill.started",
  ScribeAttempt: "scribe.prefill.attempt",
  ScribeCompositionsFound: "scribe.prefill.compositions_found",
  ScribeApplied: "scribe.prefill.applied",
  ScribeTimeout: "scribe.prefill.timeout",
  ScribeManualRetry: "scribe.prefill.manual_retry",
  ScribeManualOverride: "scribe.prefill.manual_override",
  CallsRefetch: "scribe.calls.refetch",
  FrictionDetected: "friction.detected",
} as const;

/** Union of all telemetry action names. @agentic connections */
export type TelemetryActionName =
  (typeof TelemetryActions)[keyof typeof TelemetryActions];

/** PHI-safe context: primitives only. @agentic connections */
export type TelemetryContext = Record<string, string | number | boolean>;

/**
 * Upper bound on any string attribute. Resource IDs are ~36 chars; this cap is
 * defense-in-depth against a long free-text value slipping into a trusted
 * `trackAction` call.
 * @agentic connections
 */
export const MAX_TELEMETRY_STRING_LENGTH = 120;

/**
 * Domain-event payload string keys that are safe to forward verbatim (beyond
 * `*Id` keys, which are always allowed). These are codes/statuses, never
 * free-text. Deliberately small — extend consciously.
 * @agentic connections
 */
const SAFE_EVENT_STRING_KEYS = new Set(["status", "activityCode"]);

/**
 * Domain-event payload numeric keys that are operational measures (never
 * clinical). The bridge handles UNTRUSTED payloads, so numbers are allow-listed
 * — a bare "keep every number" leaks clinical scores such as
 * `GoalProgressRecordedEvent.gasScore`. Extend consciously; never add a
 * clinical value.
 * @agentic connections
 */
const SAFE_EVENT_NUMBER_KEYS = new Set(["durationDays"]);

/**
 * Domain-event payload boolean keys that are workflow signals (never clinical).
 * Allow-listed for the same fail-safe reason as {@link SAFE_EVENT_NUMBER_KEYS}.
 * @agentic connections
 */
const SAFE_EVENT_BOOLEAN_KEYS = new Set([
  "nextActivityAdvanced",
  "carePlanCompleted",
]);

const hasCredentials = (): boolean =>
  Boolean(import.meta.env["VITE_DATADOG_APPLICATION_ID"]) &&
  Boolean(import.meta.env["VITE_DATADOG_CLIENT_TOKEN"]);

/**
 * Upper bound on string values in *error* context. More generous than action
 * context because error contexts legitimately carry React component stacks
 * (component names only — not PHI).
 * @agentic connections
 */
export const MAX_ERROR_CONTEXT_STRING_LENGTH = 2000;

/**
 * Truncate a FHIR resource id before it leaves the app. A full Patient UUID
 * is an identifier (PHI) and must not reach third-party telemetry; an 8-char
 * prefix keeps enough entropy for session-scoped correlation. Mirrors the
 * API's `maskPatientIdForLog`.
 * @agentic connections
 */
export const maskResourceId = (id: string): string =>
  id.length > 8 ? `${id.slice(0, 8)}...` : id;

/** True when a context key names an identifier (`*Id`, `*ID`, `*id`). */
const isIdKey = (key: string): boolean => /id$/i.test(key);

/** Matches a UUID (v1–v8), the shape of a Medplum/FHIR resource id. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Global variant of {@link UUID_RE} that matches a UUID appearing *anywhere*
 * inside a larger string (e.g. a `Patient/<uuid>` reference or a
 * comma-separated list of references), so embedded ids can be masked in place.
 */
const UUID_GLOBAL_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** True when a path segment names a FHIR resource type (e.g. `Patient`, `Task`). */
const isFhirResourceType = (segment: string): boolean =>
  /^[A-Z][A-Za-z]+$/.test(segment);

/**
 * Query-string keys (case-insensitive, ignoring any FHIR `:modifier` suffix)
 * that carry credentials/secrets and must be removed outright.
 */
const CREDENTIAL_QUERY_KEYS = new Set([
  "token",
  "access_token",
  "id_token",
  "refresh_token",
  "key",
  "apikey",
  "api_key",
  "code",
  "secret",
  "password",
  "authorization",
  "auth",
  "signature",
  "sig",
  "sid",
  "session",
]);

/**
 * Query-string keys (case-insensitive, ignoring any FHIR `:modifier` suffix such
 * as `name:contains`) whose values are free-text PHI — patient names, DOB,
 * contact info, MRNs, or full-text content — and must be redacted wholesale.
 * UUID masking alone is not enough: these carry human-readable identifiers, not
 * resource ids. Sources include the member-list `search` term persisted to the
 * URL (`PatientsPage`) and Medplum searches like `name:contains`, `given`,
 * `family`, and duplicate-check `given`/`family` params.
 */
const PHI_QUERY_KEYS = new Set([
  "search",
  "q",
  "name",
  "given",
  "family",
  "phonetic",
  "birthdate",
  "dob",
  "phone",
  "email",
  "telecom",
  "address",
  "address-city",
  "address-state",
  "address-postalcode",
  "address-country",
  "address-line",
  "identifier",
  "_content",
  "_text",
  // Geographic identifiers (HIPAA quasi-identifiers) and precise coordinates,
  // e.g. the HealthcareServicesPage proximity filter (`zipcode`, `lat`, `lng`).
  "zipcode",
  "zip",
  "postalcode",
  "postal-code",
  "lat",
  "lng",
  "latitude",
  "longitude",
]);

/**
 * Query-string keys whose value is itself a URL or path (which may embed PHI or
 * credentials in its own query/fragment) and must be sanitized recursively.
 * E.g. the sign-in `returnTo` param carries a same-origin path like
 * `/Patient?search=Jane` (see `app/auth/returnTo.ts`).
 */
const NESTED_URL_QUERY_KEYS = new Set([
  "returnto",
  "return",
  "redirect",
  "redirect_uri",
  "redirecturi",
  "redirecturl",
  "next",
  "continue",
  "callback",
  "url",
  "destination",
  "back",
]);

/** Placeholder written in place of a redacted free-text PHI query value. */
const REDACTED_QUERY_VALUE = "redacted";

/** Placeholder origin used to parse relative URLs / nested-URL param values. */
const PLACEHOLDER_ORIGIN = "https://redacted.invalid";

/** Mask every resource UUID embedded anywhere in a string. */
const maskUuidsInText = (value: string): string =>
  value.replace(UUID_GLOBAL_RE, (uuid) => maskResourceId(uuid));

/** Placeholder tokens written in place of each redacted PHI class in free text. */
const REDACTED_EMAIL = "[redacted-email]";
const REDACTED_DATE = "[redacted-date]";
const REDACTED_NUMBER = "[redacted-number]";

/** Minimum digit count for a numeric run to be treated as a phone/SSN/MRN. */
const MIN_PHI_DIGITS = 7;

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
/** DOB-style dates: `YYYY-MM-DD`, `MM/DD/YYYY`, `M/D/YY`, etc. */
const DATE_RE = /\b\d{1,4}[/-]\d{1,2}[/-]\d{1,4}\b/g;
/**
 * A run of digits optionally broken by spaces / punctuation used in phone,
 * SSN, or MRN formatting. The digit-count guard rejects short matches
 * (version strings, ports, HTTP status codes) so only PHI-shaped numbers go.
 */
const NUMBER_RUN_RE = /\d[\d\s().+-]{4,}\d/g;

/** Count the digit characters in a string. */
const countDigits = (value: string): number =>
  (value.match(/\d/g) ?? []).length;

/**
 * Redact PHI shapes from a free-text telemetry string: emails, resource UUIDs,
 * DOB-style dates, and phone/SSN/MRN-style numbers, then cap the length.
 *
 * RUM error events carry `error.message` and `error.stack` verbatim, and an
 * upstream Medplum/fetch error routinely interpolates the offending value (a
 * patient name reference, MRN, phone, email, or DOB) into that text. URL
 * masking in `beforeSend` does not cover the message body, so this closes the
 * symmetric leak. Redaction is deliberately aggressive — over-redacting an
 * error string is cheap; leaking one identifier is a breach. Mirrors the API's
 * `telemetry-scrub.ts`.
 * @agentic connections
 */
export const sanitizeTelemetryMessage = (text: string): string => {
  const redacted = maskUuidsInText(text)
    .replace(EMAIL_RE, REDACTED_EMAIL)
    .replace(DATE_RE, REDACTED_DATE)
    .replace(NUMBER_RUN_RE, (match) =>
      countDigits(match) >= MIN_PHI_DIGITS ? REDACTED_NUMBER : match,
    );
  return redacted.length > MAX_ERROR_CONTEXT_STRING_LENGTH
    ? `${redacted.slice(0, MAX_ERROR_CONTEXT_STRING_LENGTH)}…`
    : redacted;
};

/**
 * True when `key` (or, for chained/modified FHIR params, its trailing segment)
 * is in `set`. Drops a FHIR `:modifier` (`name:contains` → `name`) and matches
 * the segment after the last `.` (`practitioner.name` → `name`), all
 * case-insensitively.
 */
const queryKeyIn = (key: string, set: Set<string>): boolean => {
  const base = key.split(":")[0].toLowerCase();
  if (set.has(base)) return true;
  const tail = base.slice(base.lastIndexOf(".") + 1);
  return tail !== base && set.has(tail);
};

/** Mask resource-id path segments (e.g. `/Patient/<uuid>` → `/Patient/<8char>…`). */
const sanitizePathname = (pathname: string): string => {
  const segments = pathname.split("/");
  return segments
    .map((segment, index) => {
      if (!segment) return segment;
      const previous = segments[index - 1];
      const looksLikeResourceId =
        UUID_RE.test(segment) ||
        (previous !== undefined &&
          isFhirResourceType(previous) &&
          segment.length > 8);
      return looksLikeResourceId ? maskResourceId(segment) : segment;
    })
    .join("/");
};

/**
 * Sanitize a `URLSearchParams` in place: drop credential params, redact
 * free-text PHI params (incl. chained `*.name`), recursively sanitize
 * nested-URL params, and mask resource UUIDs (or `*Id` values) in the rest.
 */
const sanitizeSearchParams = (params: URLSearchParams): void => {
  for (const [key, value] of [...params.entries()]) {
    if (queryKeyIn(key, CREDENTIAL_QUERY_KEYS)) {
      params.delete(key);
      continue;
    }
    if (queryKeyIn(key, PHI_QUERY_KEYS)) {
      params.set(key, REDACTED_QUERY_VALUE);
      continue;
    }
    if (queryKeyIn(key, NESTED_URL_QUERY_KEYS)) {
      params.set(key, sanitizeTelemetryUrl(value));
      continue;
    }
    // Mask any resource UUID embedded in the value regardless of the key —
    // covers FHIR reference params (`patient`, `subject`, …) and bare or
    // `Patient/<uuid>`-style values, including comma-separated lists.
    let masked = maskUuidsInText(value);
    // Fall back to masking the whole value for a non-UUID identifier on an
    // explicit `*Id` key (e.g. a short logical id).
    if (masked === value && isIdKey(key)) masked = maskResourceId(value);
    if (masked !== value) params.set(key, masked);
  }
};

/**
 * Sanitize a URL fragment (`#…`). Fragments can carry a hash-routed path
 * (`#/Patient/<uuid>`), an explicit query (`#/x?k=v`), or a bare query payload
 * (`#access_token=…&search=…`, e.g. OAuth implicit responses). Mask UUIDs in the
 * path part and run any query payload through {@link sanitizeSearchParams}.
 */
const sanitizeUrlFragment = (hash: string): string => {
  const body = hash.startsWith("#") ? hash.slice(1) : hash;
  if (body === "") return hash;

  const queryStart = body.indexOf("?");
  if (queryStart !== -1) {
    const params = new URLSearchParams(body.slice(queryStart + 1));
    sanitizeSearchParams(params);
    const query = params.toString();
    return `#${maskUuidsInText(body.slice(0, queryStart))}${query ? `?${query}` : ""}`;
  }
  // Bare `k=v(&k=v)*` payload with no leading `?` (e.g. OAuth token fragments).
  if (body.includes("=")) {
    const params = new URLSearchParams(body);
    sanitizeSearchParams(params);
    return `#${params.toString()}`;
  }
  return `#${maskUuidsInText(body)}`;
};

/**
 * Mask FHIR/Medplum identifiers and other PHI out of a URL before it reaches
 * third-party telemetry. Datadog RUM attaches the raw browser URL to every
 * view, action, error, and resource event; our routes and Medplum requests
 * embed PHI in the path (`/Patient/<uuid>/timeline`), query, and fragment.
 * This:
 *  - masks resource-id path segments to an 8-char prefix;
 *  - drops credential/secret query params (`token`, `access_token`, …);
 *  - redacts free-text PHI query params — patient names/DOB/contact/MRN — on
 *    the member-list `search` term and Medplum name searches (`name:contains`,
 *    `given`, `family`, chained `practitioner.name`, …);
 *  - masks resource UUIDs embedded in any remaining query value (FHIR
 *    references like `?subject=Patient/<uuid>`, `?patient=<uuid>`, lists);
 *  - recursively sanitizes nested-URL params (e.g. sign-in `returnTo`);
 *  - sanitizes the fragment (hash routes and `#k=v` OAuth/query payloads);
 * while preserving the route shape so `view.name`/`view.url_path_group`
 * analytics still work.
 *
 * Relative URLs are parsed against a placeholder origin so their PHI is still
 * redacted; a truly unparseable string falls back to UUID masking.
 * @agentic connections
 */
export const sanitizeTelemetryUrl = (rawUrl: string): string => {
  try {
    const url = new URL(rawUrl);
    sanitizeSearchParams(url.searchParams);
    url.pathname = sanitizePathname(url.pathname);
    if (url.hash) url.hash = sanitizeUrlFragment(url.hash);
    return url.toString();
  } catch {
    // Not an absolute URL. If it looks like a path/query/fragment, parse it
    // against a placeholder origin so relative-URL PHI is still redacted;
    // otherwise fall back to masking any embedded UUID.
    if (/^[/?#]/.test(rawUrl) || rawUrl.includes("?") || rawUrl.includes("#")) {
      try {
        const url = new URL(rawUrl, PLACEHOLDER_ORIGIN);
        sanitizeSearchParams(url.searchParams);
        url.pathname = sanitizePathname(url.pathname);
        if (url.hash) url.hash = sanitizeUrlFragment(url.hash);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch {
        // fall through to UUID masking
      }
    }
    return maskUuidsInText(rawUrl);
  }
};

/**
 * Keep a single primitive value if it is safe to send, else return `undefined`.
 * Strings are trimmed to {@link MAX_TELEMETRY_STRING_LENGTH}; non-finite numbers
 * and non-primitives are dropped.
 * @agentic connections
 */
const keepPrimitive = (
  value: unknown,
): string | number | boolean | undefined => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    return value.length > MAX_TELEMETRY_STRING_LENGTH
      ? value.slice(0, MAX_TELEMETRY_STRING_LENGTH)
      : value;
  }
  return undefined;
};

/**
 * Sanitize a **caller-constructed** context: trusts the key names (feature code
 * chooses them) but enforces primitives-only and the string cap. Used by
 * {@link trackAction}.
 * @agentic connections
 */
export const sanitizeTelemetryContext = (
  context: Record<string, unknown>,
): TelemetryContext => {
  const out: TelemetryContext = {};
  for (const [key, value] of Object.entries(context)) {
    const kept = keepPrimitive(value);
    if (kept === undefined) continue;
    out[key] =
      typeof kept === "string" && isIdKey(key) ? maskResourceId(kept) : kept;
  }
  return out;
};

/**
 * Sanitize a caller-supplied `reportError` context before it reaches Datadog
 * RUM: primitives only (objects — e.g. FHIR resources — are dropped),
 * identifier values (`*Id` keys) are masked, and strings are capped at
 * {@link MAX_ERROR_CONTEXT_STRING_LENGTH} so long free-text cannot leak
 * wholesale into telemetry.
 * @agentic connections
 */
export const sanitizeErrorContext = (
  context: Record<string, unknown>,
): TelemetryContext => {
  const out: TelemetryContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      continue;
    }
    if (typeof value === "number" && !Number.isFinite(value)) continue;
    if (typeof value === "string") {
      const masked = isIdKey(key) ? maskResourceId(value) : value;
      out[key] =
        masked.length > MAX_ERROR_CONTEXT_STRING_LENGTH
          ? masked.slice(0, MAX_ERROR_CONTEXT_STRING_LENGTH)
          : masked;
    } else {
      out[key] = value;
    }
  }
  return out;
};

/**
 * Extract a PHI-safe context from an **untrusted** domain-event payload. Every
 * value type is allow-listed so nothing leaks by default:
 *
 *  - strings — kept only for identifiers (`*Id`) or allow-listed codes/statuses
 *    (drops free-text like a pause `reason`);
 *  - numbers — kept only for allow-listed operational measures (drops clinical
 *    scores such as `GoalProgressRecordedEvent.gasScore`);
 *  - booleans — kept only for allow-listed workflow signals;
 *  - objects/arrays (e.g. full FHIR resources) — always dropped.
 *
 * @agentic connections
 */
export const extractEventContext = (payload: unknown): TelemetryContext => {
  if (!payload || typeof payload !== "object") return {};
  const out: TelemetryContext = {};
  for (const [key, value] of Object.entries(
    payload as Record<string, unknown>,
  )) {
    const isAllowed =
      typeof value === "string"
        ? /id$/i.test(key) || SAFE_EVENT_STRING_KEYS.has(key)
        : typeof value === "number"
          ? SAFE_EVENT_NUMBER_KEYS.has(key)
          : typeof value === "boolean"
            ? SAFE_EVENT_BOOLEAN_KEYS.has(key)
            : false;
    if (!isAllowed) continue;
    const kept = keepPrimitive(value);
    if (kept === undefined) continue;
    out[key] =
      typeof kept === "string" && isIdKey(key) ? maskResourceId(kept) : kept;
  }
  return out;
};

/**
 * Record a custom RUM action. No-op without credentials. Never throws into the
 * caller — observability must not break a clinical workflow.
 * @agentic connections
 */
export const trackAction = (
  name: TelemetryActionName,
  context: Record<string, unknown> = {},
): void => {
  if (!hasCredentials()) return;
  try {
    datadogRum.addAction(name, sanitizeTelemetryContext(context));
  } catch {
    // Swallow — telemetry failures are never surfaced to the user.
  }
};

/**
 * Report a feature-flag evaluation to RUM so sessions/errors can be sliced by
 * rollout. No-op without credentials; never throws.
 * @agentic connections
 */
export const trackFeatureFlagEvaluation = (
  key: string,
  value: boolean,
): void => {
  if (!hasCredentials()) return;
  try {
    datadogRum.addFeatureFlagEvaluation(key, value);
  } catch {
    // Swallow — observability must not break the app.
  }
};
